import { Component, ViewChild, OnInit } from '@angular/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { NotificationService } from 'app/_services/notification.service';
import { environment } from '../../environments/environment';
import { DynamicFormComponent } from '../_dynamic_form/containers/dynamic_form/dynamic-form.component';
import { TreeNode } from 'primeng/api';
import { ProductsService } from '../_services/products.service';
import { CategoryService } from '../_services/category.service';
import { KioskCategoryService } from '../_services/kiosk-category.service';
import { ObjectFilterPipe } from '../_pipes/object-filter.pipe';
import { CookieService } from 'ngx-cookie-service';

@Component({
    selector: 'app-products',
    templateUrl: './products.component.html',
    styleUrls: ['./products.component.css'],
})
export class ProductsComponent implements OnInit {
    @ViewChild(DynamicFormComponent) filterForm: DynamicFormComponent;

    products: any[] = [];
    selectedProduct: any;

    storeId: any;
    cookieStore: any;

    filters: any;

    sidebarVisible = false;
    createProductModal = false;
    createProductForm: FormGroup;
    errMsgs: any[] = [];

    pageSizes: any = environment.pageSizes;
    pagination: any;
    manualPageNumber = 1;
    manualPageSize = this.pageSizes[0];

    selectedSort: any;

    sortFields: any[] = [
        {
            sortName: 'Name (asc)',
            sortString: 'name,asc',
        },
        {
            sortName: 'Name (desc)',
            sortString: 'name,desc',
        },
        {
            sortName: 'Price (asc)',
            sortString: 'price,asc',
        },
        {
            sortName: 'Price (desc)',
            sortString: 'price,desc',
        },
        {
            sortName: 'Category (asc)',
            sortString: 'itemCategory,asc',
        },
        {
            sortName: 'Category (desc)',
            sortString: 'itemCategory,desc',
        },
    ];

    filterFields: any[] = [
        {
            type: 'input',
            label: 'Name',
            name: 'name',
            placeholder: 'Enter product name',
        },
        {
            type: 'input',
            label: 'Price',
            name: 'price',
            placeholder: 'Enter product price',
        },
        {
            type: 'input',
            label: 'EAN',
            name: 'ean',
            placeholder: 'Enter product EAN',
        },
        {
            label: 'Submit',
            name: 'submit',
            type: 'button',
        },
    ];

    displayCategories: TreeNode[] = [];
    categories: any[] = [];
    selectedCategory = {
        label: 'No Category Selected',
        id: null,
        name: null,
        data: { id: null },
    };

    displayKioskCategories: TreeNode[] = [];
    kioskCategories: any[] = [];
    selectedKioskCategory = {
        label: 'No Kiosk Category Selected',
        id: null,
        data: { id: null },
    };

    constructor(
        public loader: LoadingBarService,
        public activatedRoute: ActivatedRoute,
        public router: Router,
        public globals: Globals,
        public fb: FormBuilder,
        public notificationService: NotificationService,
        public _productsService: ProductsService,
        public _categoryService: CategoryService,
        public _kioskCategoryService: KioskCategoryService,
        public objectFilter: ObjectFilterPipe,
        public cookieService: CookieService
    ) {
        this.createProductForm = fb.group({
            name: new FormControl('', [Validators.required]),
            displayName: new FormControl('', [Validators.required]),
            price: new FormControl('', [Validators.required]),
            description: new FormControl(''),
            ean: new FormControl(''),
            productCode: new FormControl(''),
            sortOrder: new FormControl(''),
            allergensApiProductId: new FormControl(''),
            itemCategoryId: new FormControl(''),
            itemCategoryKioskId: new FormControl(''),
        });

        // Handle Store Cookies
        const hasCurrentStore = cookieService.check('currentStore');
        if (hasCurrentStore) {
            this.cookieStore = JSON.parse(cookieService.get('currentStore'));
            this.storeId = this.cookieStore.id;
        } else {
            this.router.navigate(['/' + this.globals.user.routeRole + '/select-store']);
        }

        this.selectedSort = this.sortFields[0];
        this.resetPagination();
    }

    ngOnInit() {
        this.getAllProducts();
        this.getAllCategories();
        this.getAllKioskCategories();
    }

    public getAllProducts() {
        this._productsService
            .getAllProductsByStoreId(
                this.storeId,
                this.pagination,
                this.selectedSort.sortString,
                this.filters
            )
            .subscribe((data: any) => {
                this.products = data.body;
                // // console.log('Products ', this.products);
                this.pagination.totalRecords = data.headers.get('x-total-count');
            });
    }

    /* PAGINATION */
    public resetPagination() {
        this.pagination = {
            size: this.pageSizes[0],
            page: 0,
            totalRecords: 0,
        };
        this.manualPageNumber = 1;
        this.manualPageSize = this.pageSizes[0];
        this.getAllProducts();
    }

    paginate(event) {
        // // console.log(event);
        this.pagination.size = event.rows;
        this.pagination.page = event.page;
        this.manualPageNumber = event.page + 1;
        this.manualPageSize = event.rows;
        this.getAllProducts();
    }

    manualPaginate(event) {
        // // console.log(event);
        if (event) {
            this.paginate({
                rows: this.manualPageSize,
                page: event - 1,
            });
        }
    }
    /* PAGINATION END */

    // Handle onRowSelect (ie Select Single Patient and navigate to inner view)
    onRowSelect(event) {
        this.router.navigate(['/' + this.globals.user.routeRole + '/products/' + event.data.id]);
    }

    public toggleCreateProductModal() {
        this.createProductModal = !this.createProductModal;
    }

    public categorySelected(event) {
        // // console.log(event);
        this.createProductForm.patchValue({
            category: event.node,
        });
    }

    public kioskCategorySelected(event) {
        // // console.log(event);
        this.createProductForm.patchValue({
            kioskCategory: event.node,
        });
    }

    public onCreateProductFormSubmit() {
        const values = this.createProductForm.value;
        const body = {
            description: values.description,
            displayName: values.displayName,
            ean: values.ean,
            itemCategoryId: values.itemCategoryId,
            itemCategoryKioskId: values.itemCategoryKioskId,
            name: values.name,
            price: values.price,
            productCode: values.productCode,
            sortOrder: values.sortOrder,
            allergensApiProductId: values.sortOrder,
            storeId: this.storeId,
        };
        this._productsService.createProduct(body).subscribe((data: any) => {
            // // console.log(data);
            this.createProductForm.reset();
            this.getAllProducts();
            this.toggleCreateProductModal();
            this.notificationService.notify(
                'success',
                'Product Created',
                'New Product has successfully been created',
                'top',
                'right'
            );
        });
    }

    // FILTER AND SORTING

    sortChange(event) {
        this.selectedSort = event;
        this.resetPagination();
        this.getAllProducts();
    }

    filterFormSubmitted(value) {
        this.sidebarVisible = false;
        this.filters = value;
        this.resetPagination();
        this.getAllProducts();
    }

    clearFilters() {
        this.sidebarVisible = false;
        this.filters = '';
        this.filterForm.clearForm();
        this.resetPagination();
        this.getAllProducts();
    }

    // ************************************************************************* //
    // Functions to get, setup and handle Category Tree population and functions //
    // ************************************************************************* //

    public getAllCategories() {
        this.displayCategories = [];
        this._categoryService.getItemCategoriesByStoreId(this.storeId).subscribe((data: any) => {
            this.categories = data;
            this.filterRootItemCategories(null);
        });
    }

    public getAllKioskCategories() {
        this.displayKioskCategories = [];
        this._kioskCategoryService
            .getItemKioskCategoriesByStoreId(this.storeId)
            .subscribe((data: any) => {
                this.kioskCategories = data;
                this.filterRootItemKioskCategories(null);
            });
    }

    public populateTreeMenu(array) {
        const filteredArray = array;
        filteredArray.forEach((element) => {
            // // console.log(element);
            const treeNodeObject = {
                label: element.name,
                data: element,
                id: element.id,
                parent: element.parentItemCategoryId,
                expandedIcon: 'fa fa-folder-open',
                collapsedIcon: 'fa fa-folder',
                leaf: false,
            };
            this.displayCategories.push(treeNodeObject);
        });
    }

    public populateKioskTreeMenu(array) {
        const filteredArray = array;
        filteredArray.forEach((element) => {
            // // console.log(element);
            const treeNodeObject = {
                label: element.name,
                data: element,
                id: element.id,
                parent: element.parentItemCategoryId,
                expandedIcon: 'fa fa-folder-open',
                collapsedIcon: 'fa fa-folder',
                leaf: false,
            };
            this.displayKioskCategories.push(treeNodeObject);
        });
    }

    public filterRootItemCategories(categoryId) {
        const catId = categoryId;
        const filteredCats = this.objectFilter.transform(
            this.categories,
            'parentItemCategoryId',
            catId
        );
        this.populateTreeMenu(filteredCats);
    }

    public filterRootItemKioskCategories(kioskCategoryId) {
        const catId = kioskCategoryId;
        const filteredCats = this.objectFilter.transform(
            this.kioskCategories,
            'parentItemCategoryId',
            catId
        );
        this.populateKioskTreeMenu(filteredCats);
    }

    public loadNode(event) {
        if (event.node) {
            const parentId = event.node.data.id;
            const children = this.objectFilter.transform(
                this.categories,
                'parentItemCategoryId',
                parentId
            );
            children.forEach((element) => {
                element.label = element.name;
                element.data = element;
                element.parent = element.parentItemCategoryId;
                element.expandedIcon = 'fa fa-folder-open';
                element.collapsedIcon = 'fa fa-folder';
                element.leaf = false;
            });
            event.node.children = children;
        }
    }

    public loadNodeKiosk(event) {
        if (event.node) {
            const parentId = event.node.data.id;
            const children = this.objectFilter.transform(
                this.kioskCategories,
                'parentItemCategoryId',
                parentId
            );
            children.forEach((element) => {
                element.label = element.name;
                element.data = element;
                element.parent = element.parentItemCategoryId;
                element.expandedIcon = 'fa fa-folder-open';
                element.collapsedIcon = 'fa fa-folder';
                element.leaf = false;
            });
            event.node.children = children;
        }
    }

    public nodeSelect(event) {
        this.selectedCategory.label = event.node.data.name;
        this.selectedCategory.id = event.node.data.id;
        this.selectedCategory.data = event.node.data;
        this.createProductForm.patchValue({
            itemCategoryId: this.selectedCategory.id,
        });
    }

    public nodeSelectKiosk(event) {
        this.selectedKioskCategory.label = event.node.data.name;
        this.selectedKioskCategory.id = event.node.data.id;
        this.selectedKioskCategory.data = event.node.data;
        this.createProductForm.patchValue({
            itemCategoryKioskId: this.selectedKioskCategory.id,
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/products/products.component.ts